"use strict";
// ---------------------------------------------------------------------------
// Feedback — pure URL assembly + context types.
//
// PURE: no IO, no side effects. The app layer gathers the raw facts (resolved
// repo, Loom version, OS, usage counts) and this module turns them into the
// prefilled GitHub issue-form URL. Keeping the assembly here means the CLI, the
// MCP resource, and the extension all build the exact same URL from the same
// rules, and the encoding is unit-testable without touching the filesystem.
// ---------------------------------------------------------------------------
Object.defineProperty(exports, "__esModule", { value: true });
exports.DEFAULT_FEEDBACK_REPO = exports.FEEDBACK_TEMPLATE_FILE = void 0;
exports.resolveFeedbackRepo = resolveFeedbackRepo;
exports.formatFeedbackEnvironment = formatFeedbackEnvironment;
exports.buildFeedbackUrl = buildFeedbackUrl;
/** Issue-form template filename the central sink repo hosts. */
exports.FEEDBACK_TEMPLATE_FILE = 'feedback.yml';
/**
 * The canonical feedback sink: EVERY Loom install — this repo, chord-flow, any
 * end-user project — files feedback into the Loom project's issues, so the
 * signal lands where it's acted on. This is deliberately central, not the
 * current project's repo: a user reporting a Loom bug wants the Loom maintainer
 * to see it, not to file it in their own repo where it's lost.
 */
exports.DEFAULT_FEEDBACK_REPO = 'reslava/loom';
/**
 * Resolve the feedback target: an explicit override, else the central sink.
 * Pure. The override exists for *code reuse* — a fork or a non-Loom tool built
 * on this mechanism points feedback at its own repo (e.g. via the
 * `reslava-loom.feedback.repo` setting / `--repo` flag). Absent that, feedback
 * always reaches the Loom project; it never depends on the current git remote.
 */
function resolveFeedbackRepo(override) {
    const o = override?.trim();
    return o ? o : exports.DEFAULT_FEEDBACK_REPO;
}
/**
 * Render the snapshot as the human-readable body of the form's `environment`
 * field. Plain text, one fact per line, non-PII.
 */
function formatFeedbackEnvironment(s) {
    return [
        `Loom version: ${s.loomVersion}`,
        `OS: ${s.platform}`,
        `Weaves: ${s.weaveCount}`,
        `Threads: ${s.threadCount}`,
        `Done plans: ${s.donePlanCount}`,
        `Current release: ${s.currentRelease ?? 'n/a'}`,
    ].join('\n');
}
/**
 * Build a prefilled GitHub issue-form URL. Pure. GitHub issue *forms* prefill
 * fields by their YAML `id` via `?<field_id>=<value>` (URL-encoded); here the
 * `environment` textarea id must match the sink repo's form, and `template=`
 * targets the form itself.
 */
function buildFeedbackUrl(params) {
    const base = `https://github.com/${params.repo}/issues/new`;
    const query = new URLSearchParams({
        template: exports.FEEDBACK_TEMPLATE_FILE,
        environment: formatFeedbackEnvironment(params.snapshot),
    });
    return `${base}?${query.toString()}`;
}
